/**
 * Real-Time Conversations Hook
 * 
 * Integrates WebSocket real-time updates with the existing conversation management system.
 * Provides automatic updates without replacing the current architecture.
 */

import { useEffect, useCallback, useState, useRef } from 'react'
import { realTimeConversationsService } from '../services/realTimeConversations'
import { logger } from '../utils/logger'
import { getValidToken } from '../utils/tokenUtils'

export interface UseRealTimeConversationsOptions {
  /** Enable real-time updates */
  enabled?: boolean
  /** Auto-subscribe to conversation list updates */
  subscribeToList?: boolean
  /** Auto-subscribe to agent presence updates */
  subscribeToAgentPresence?: boolean
  /** Callback when conversation list should be refreshed */
  onRefreshNeeded?: () => void
  /** Callback when specific conversation needs update */
  onConversationUpdate?: (conversationId: string, data: any) => void
  /** Callback when agent presence changes */
  onAgentPresenceUpdate?: (data: any) => void
}

export interface UseRealTimeConversationsReturn {
  /** WebSocket connection status */
  isConnected: boolean
  /** Connection state */
  connectionState: string
  /** Last update timestamp */
  lastUpdate: Date | null
  /** Error state */
  error: string | null
  /** Connect to WebSocket */
  connect: () => void
  /** Disconnect from WebSocket */
  disconnect: () => void
  /** Subscribe to specific conversation */
  subscribeToConversation: (conversationId: string) => void
  /** Unsubscribe from specific conversation */
  unsubscribeFromConversation: (conversationId: string) => void
  /** Force refresh */
  forceRefresh: () => void
}

/**
 * Real-time conversations hook for enhanced live updates
 */
export function useRealTimeConversations({
  enabled = true,
  subscribeToList = true,
  subscribeToAgentPresence = true,
  onRefreshNeeded,
  onConversationUpdate,
  onAgentPresenceUpdate
}: UseRealTimeConversationsOptions = {}): UseRealTimeConversationsReturn {
  
  const [isConnected, setIsConnected] = useState(false)
  const [connectionState, setConnectionState] = useState('disconnected')
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // Debounce connection state updates to prevent flashing
  const [debouncedIsConnected, setDebouncedIsConnected] = useState(false)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  // Update connection state with debouncing
  const updateConnectionState = useCallback(() => {
    const currentConnected = realTimeConversationsService.isConnected
    const currentState = realTimeConversationsService.connectionState
    
    setIsConnected(currentConnected)
    setConnectionState(currentState)
    
    // Debounce the UI-visible connection state to prevent flashing
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedIsConnected(currentConnected)
    }, currentConnected ? 100 : 1000) // Quick connect, slower disconnect
  }, [])
  
  // Handle connection events
  const handleConnected = useCallback(() => {
    logger.info('🌐 Real-time conversations connected')
    updateConnectionState()
    setError(null)
    
    // Auto-subscribe to updates
    if (subscribeToList) {
      logger.info('📋 Subscribing to conversation list updates')
      realTimeConversationsService.subscribeToConversationList()
    }
    
    if (subscribeToAgentPresence) {
      logger.info('👤 Subscribing to agent presence updates')
      realTimeConversationsService.subscribeToAgentPresence()
    }
  }, [subscribeToList, subscribeToAgentPresence, updateConnectionState])
  
  const handleDisconnected = useCallback((data: any) => {
    logger.info('🔌 Real-time conversations disconnected', data)
    updateConnectionState()
    setError('Disconnected from server')
  }, [updateConnectionState])
  
  const handleError = useCallback((data: any) => {
    logger.error('❌ Real-time conversations error', data)
    setError(data.error || 'Connection error')
    updateConnectionState()
  }, [updateConnectionState])
  
  // Handle conversation list updates
  const handleConversationListUpdate = useCallback((data: any) => {
    logger.info('📋 Conversation list update received', data)
    setLastUpdate(new Date())
    onRefreshNeeded?.()
  }, [onRefreshNeeded])
  
  // Handle specific conversation updates
  const handleConversationUpdate = useCallback((data: any) => {
    logger.info('💬 Conversation update received', data)
    setLastUpdate(new Date())
    
    if (data.conversation_id) {
      onConversationUpdate?.(data.conversation_id, data)
    }
    
    // Also trigger general refresh
    onRefreshNeeded?.()
  }, [onConversationUpdate, onRefreshNeeded])
  
  // Handle new messages
  const handleNewMessage = useCallback((data: any) => {
    logger.info('📨 New message received', data)
    setLastUpdate(new Date())
    
    if (data.conversation_id) {
      onConversationUpdate?.(data.conversation_id, data)
    }
    
    // Trigger refresh to update conversation list
    onRefreshNeeded?.()
  }, [onConversationUpdate, onRefreshNeeded])
  
  // Handle status changes
  const handleStatusChange = useCallback((data: any) => {
    logger.info('🔄 Status change received', data)
    setLastUpdate(new Date())
    
    if (data.conversation_id) {
      onConversationUpdate?.(data.conversation_id, data)
    }
    
    onRefreshNeeded?.()
  }, [onConversationUpdate, onRefreshNeeded])
  
  // Handle assignment changes
  const handleAssignmentChange = useCallback((data: any) => {
    logger.info('👥 Assignment change received', data)
    setLastUpdate(new Date())
    
    if (data.conversation_id) {
      onConversationUpdate?.(data.conversation_id, data)
    }
    
    onRefreshNeeded?.()
  }, [onConversationUpdate, onRefreshNeeded])
  
  // Handle agent presence updates
  const handleAgentPresenceUpdate = useCallback((data: any) => {
    logger.info('👤 Agent presence update received', data)
    setLastUpdate(new Date())
    onAgentPresenceUpdate?.(data)
  }, [onAgentPresenceUpdate])
  
  // Setup event listeners
  useEffect(() => {
    if (!enabled) return
    
    // Only connect if user is authenticated with valid token
    const token = getValidToken()
    if (!token) {
      logger.debug('🔌 Skipping real-time conversations - no valid auth token')
      return
    }
    
    // Initialize the service if not already connected
    realTimeConversationsService.initializeAfterAuth()
    
    // Connection events
    realTimeConversationsService.addEventListener('connected', handleConnected)
    realTimeConversationsService.addEventListener('disconnected', handleDisconnected)
    realTimeConversationsService.addEventListener('error', handleError)
    
    // Conversation events
    realTimeConversationsService.addEventListener('conversation_list_update', handleConversationListUpdate)
    realTimeConversationsService.addEventListener('conversation_update', handleConversationUpdate)
    realTimeConversationsService.addEventListener('new_message', handleNewMessage)
    realTimeConversationsService.addEventListener('status_change', handleStatusChange)
    realTimeConversationsService.addEventListener('assignment_change', handleAssignmentChange)
    
    // Agent events
    realTimeConversationsService.addEventListener('agent_presence_update', handleAgentPresenceUpdate)
    
    // Initial state update
    updateConnectionState()
    
    return () => {
      // Cleanup event listeners
      realTimeConversationsService.removeEventListener('connected', handleConnected)
      realTimeConversationsService.removeEventListener('disconnected', handleDisconnected)
      realTimeConversationsService.removeEventListener('error', handleError)
      realTimeConversationsService.removeEventListener('conversation_list_update', handleConversationListUpdate)
      realTimeConversationsService.removeEventListener('conversation_update', handleConversationUpdate)
      realTimeConversationsService.removeEventListener('new_message', handleNewMessage)
      realTimeConversationsService.removeEventListener('status_change', handleStatusChange)
      realTimeConversationsService.removeEventListener('assignment_change', handleAssignmentChange)
      realTimeConversationsService.removeEventListener('agent_presence_update', handleAgentPresenceUpdate)
    }
  }, [
    enabled,
    handleConnected,
    handleDisconnected,
    handleError,
    handleConversationListUpdate,
    handleConversationUpdate,
    handleNewMessage,
    handleStatusChange,
    handleAssignmentChange,
    handleAgentPresenceUpdate,
    updateConnectionState
  ])
  
  // Connect/disconnect functions
  const connect = useCallback(() => {
    realTimeConversationsService.connect()
  }, [])
  
  const disconnect = useCallback(() => {
    realTimeConversationsService.disconnect()
  }, [])
  
  // Subscription functions
  const subscribeToConversation = useCallback((conversationId: string) => {
    realTimeConversationsService.subscribeToConversation(conversationId)
  }, [])
  
  const unsubscribeFromConversation = useCallback((conversationId: string) => {
    realTimeConversationsService.unsubscribeFromConversation(conversationId)
  }, [])
  
  // Force refresh function
  const forceRefresh = useCallback(() => {
    logger.info('🔄 Forcing conversation list refresh')
    onRefreshNeeded?.()
  }, [onRefreshNeeded])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])
  
  return {
    isConnected: debouncedIsConnected, // Use debounced state for UI
    connectionState,
    lastUpdate,
    error,
    connect,
    disconnect,
    subscribeToConversation,
    unsubscribeFromConversation,
    forceRefresh
  }
}

export default useRealTimeConversations